import { useCallback, useEffect, useRef, useState } from 'react';
import { candidatesInBbox, acceptCandidate, rejectCandidate } from '../../lib/buildingCandidatesApi';
import { polygonGeometryToPath } from '../../utils/geoJson';
import './BuildingCandidateReview.css';

const number = (value) => Number(value).toLocaleString('fa-IR', { maximumFractionDigits: 1 });
const confidence = (value) => value == null ? 'نامشخص' : `${number(value * 100)}٪`;

// Project the anchor into screen pixels, then move it only if the control panel
// covers it. Pick the largest remaining map rectangle (below the panel on mobile).
function revealCandidate(map, container, row) {
  const projection = map?.getProjection?.();
  const bounds = map?.getBounds();
  const mapRect = map?.getDiv?.().getBoundingClientRect();
  const panel = container?.parentElement?.getBoundingClientRect();
  if (!projection || !bounds?.getNorthEast || !mapRect || !panel) return;
  const point = projection.fromLatLngToPoint(new window.google.maps.LatLng(row.lat, row.lng));
  const ne = projection.fromLatLngToPoint(bounds.getNorthEast());
  const sw = projection.fromLatLngToPoint(bounds.getSouthWest());
  const scale = 2 ** map.getZoom();
  const x = mapRect.left + (point.x - sw.x) * scale;
  const y = mapRect.top + (point.y - ne.y) * scale;
  const left = Math.max(mapRect.left, panel.left - 24);
  const right = Math.min(mapRect.right, panel.right + 24);
  const top = Math.max(mapRect.top, panel.top - 24);
  const bottom = Math.min(mapRect.bottom, panel.bottom + 24);
  if (x < left || x > right || y < top || y > bottom) return;
  const spaces = [
    [mapRect.left, mapRect.top, left, mapRect.bottom],
    [right, mapRect.top, mapRect.right, mapRect.bottom],
    [mapRect.left, mapRect.top, mapRect.right, top],
    [mapRect.left, bottom, mapRect.right, mapRect.bottom],
  ].filter(([l, t, r, b]) => r - l > 48 && b - t > 48)
    .sort((a, b) => (b[2] - b[0]) * (b[3] - b[1]) - (a[2] - a[0]) * (a[3] - a[1]));
  if (spaces.length) {
    const [l, t, r, b] = spaces[0];
    map.panBy(x - (l + r) / 2, y - (t + b) / 2);
  }
}
const dashIcons = (selected) => [{ icon: { path: 'M 0,-1 0,1',
  strokeColor: '#fbbf24', strokeOpacity: 1, strokeWeight: selected ? 3 : 2, scale: 2 }, offset: '0', repeat: '10px' }];

export default function BuildingCandidateReview({ map, canReview = false, blocked, onAccepted, onSelect }) {
  const [enabled, setEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [reason, setReason] = useState('ساختمان نیست');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [revision, setRevision] = useState(0);
  const actionRef = useRef(false);
  const reviewedRef = useRef(new Set());
  const reloadFailedRef = useRef(null);
  const loadControllerRef = useRef(null);
  const panelRef = useRef(null);
  const sectionRef = useRef(null);
  const nextRef = useRef(null);
  const focusNextRef = useRef(false);
  useEffect(() => {
    if (!busy && focusNextRef.current) { nextRef.current?.focus({ preventScroll: true }); focusNextRef.current = false; }
  }, [busy]);
  const panFrameRef = useRef(null);
  const overlaysRef = useRef(new Map());
  const styledSelectionRef = useRef(null);
  const interactionRef = useRef(null);
  interactionRef.current = { blocked, busy, selectedId, onSelect };
  const selected = rows.find((row) => row.id === selectedId);

  // No background requests while off or unauthorized. Cancel stale viewport loads.
  useEffect(() => {
    if (!map || !enabled || !canReview) {
      setRows([]); setCount(null); setLoading(false); setNotice(null); setError(null); setSelectedId(null);
      return undefined;
    }
    let timer;
    let controller;
    let disposed = false;
    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      loadControllerRef.current = controller;
      const current = controller;
      const bounds = map.getBounds();
      if (!bounds) return;
      const viewport = bounds.toJSON();
      if ((map.getZoom() || 0) < 11 || viewport.east - viewport.west > 0.050000001
          || viewport.north - viewport.south > 0.050000001) {
        setRows([]); setCount(null); setLoading(false); setError(null); setSelectedId(null);
        setNotice('برای بررسی پیشنهادها بیشتر زوم کنید.');
        return;
      }
      setLoading(true); setError(null); setNotice(null);
      const response = await candidatesInBbox(viewport, false, current.signal);
      if (disposed || current.signal.aborted || response.aborted) return;
      setLoading(false);
      if (response.error) {
        setRows([]); setCount(null); setNotice(null);
        if (response.code === 'CANDIDATE_BBOX') { setNotice('برای بررسی پیشنهادها بیشتر زوم کنید.'); return; }
        setError({ message: response.error, retryable: response.retryable || ![401, 403, 429].includes(response.status) });
        return;
      }
      setCount(response.pending_count);
      setRows((response.rows || []).filter((row) => !reviewedRef.current.has(row.id)));
      setNotice(response.truncated ? 'پیشنهادهای بیشتری وجود دارد؛ برای نمایش همه بیشتر زوم کنید.' : null);
    };
    const schedule = () => { controller?.abort(); clearTimeout(timer); timer = setTimeout(load, 250); };
    const listener = map.addListener('idle', schedule);
    load();
    return () => { disposed = true; clearTimeout(timer); controller?.abort(); window.google.maps.event.removeListener(listener); };
  }, [map, enabled, canReview, revision]);

  const choose = useCallback((id) => {
    setSelectedId(id); setError(null); setReason('ساختمان نیست'); interactionRef.current.onSelect?.();
  }, []);

  useEffect(() => {
    if (!enabled || !canReview || !map) return undefined;
    const overlays = overlaysRef.current;
    rows.forEach((row) => {
      const paths = row.geometry.coordinates.map((ring) => polygonGeometryToPath({ type: 'Polygon', coordinates: [ring] }));
      const state = interactionRef.current;
      // Only the invisible polygon is interactive. Solid casing remains visible
      // across roofs and light ground; dashes draw each ring, including holes.
      const hit = new window.google.maps.Polygon({ paths, fillOpacity: 0, strokeOpacity: 0,
        clickable: !state.blocked && !state.busy, zIndex: 10, map });
      hit.addListener('click', () => {
        if (!interactionRef.current.blocked && !interactionRef.current.busy) choose(row.id);
      });
      const casings = [];
      const dashes = [];
      paths.forEach((ring) => {
        const path = [...ring, ring[0]];
        casings.push(new window.google.maps.Polyline({ path, map, clickable: false,
          strokeColor: '#0f172a', strokeOpacity: 1, strokeWeight: 5, zIndex: 8 }));
        dashes.push(new window.google.maps.Polyline({ path, map, clickable: false,
          strokeOpacity: 0, zIndex: 9, icons: dashIcons(row.id === state.selectedId) }));
      });
      overlays.set(row.id, { hit, casings, dashes });
    });
    return () => {
      overlays.forEach(({ hit, casings, dashes }) => [hit, ...casings, ...dashes].forEach((o) => o.setMap(null)));
      overlays.clear();
    };
  }, [map, enabled, canReview, rows, choose]);

  useEffect(() => {
    for (const id of new Set([styledSelectionRef.current, selectedId])) {
      overlaysRef.current.get(id)?.dashes.forEach((line) => line.setOptions({ icons: dashIcons(id === selectedId) }));
    }
    styledSelectionRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    overlaysRef.current.forEach(({ hit }) => hit.setOptions({ clickable: !blocked && !busy }));
  }, [blocked, busy]);

  // Row objects change on idle. Selection identity changes only by user choice.
  useEffect(() => { if (selectedId) panelRef.current?.focus({ preventScroll: true }); }, [selectedId]);
  useEffect(() => () => cancelAnimationFrame(panFrameRef.current), []);
  const next = useCallback(() => {
    if (!rows.length) return;
    const index = rows.findIndex((row) => row.id === selectedId);
    const row = rows[(index + 1) % rows.length];
    choose(row.id);
    cancelAnimationFrame(panFrameRef.current);
    // Wait for the details to change the panel height before measuring coverage.
    panFrameRef.current = requestAnimationFrame(() => revealCandidate(map, sectionRef.current, row));
  }, [rows, selectedId, choose, map]);

  const review = useCallback(async (action) => {
    if (!selected || blocked || actionRef.current) return;
    actionRef.current = true; setBusy(true); setError(null);
    try {
      const response = action === 'accept' ? await acceptCandidate(selected.id) : await rejectCandidate(selected.id, reason);
      if (response.error) { setError({ message: response.error, retryable: !!response.retryable }); return; }
      focusNextRef.current = true;
      loadControllerRef.current?.abort();
      setLoading(false);
      reviewedRef.current.add(selected.id);
      setRows((current) => current.filter((row) => row.id !== selected.id));
      setCount((current) => current == null ? current : Math.max(0, current - 1));
      setSelectedId(null);
      if (action === 'accept') {
        try { await onAccepted(selected); reloadFailedRef.current = null; }
        catch { reloadFailedRef.current = selected; setError({ message: 'ساختمان تأیید شد، اما نقشه تازه نشد؛ برای بارگذاری دوباره تلاش کنید.', retryable: true }); }
      }
    } catch { setError({ message: 'بررسی ناموفق بود؛ دوباره تلاش کنید.', retryable: true }); }
    finally { actionRef.current = false; setBusy(false); }
  }, [selected, blocked, reason, onAccepted]);

  useEffect(() => {
    if (!enabled || !canReview || blocked || busy) return undefined;
    const keydown = (event) => {
      const target = event.target;
      if (event.repeat || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey
          || target?.closest?.('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]), textarea, select')
          || target?.isContentEditable || target?.closest?.('[contenteditable]:not([contenteditable="false"])')) return;
      if (event.code === 'KeyN') { event.preventDefault(); next(); }
      if (selected && ['KeyA', 'KeyR'].includes(event.code)) {
        event.preventDefault(); review(event.code === 'KeyA' ? 'accept' : 'reject');
      }
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, [enabled, canReview, blocked, busy, selected, next, review]);

  const retry = async () => {
    if (!reloadFailedRef.current) { setRevision((v) => v + 1); return; }
    setBusy(true);
    try { await onAccepted(reloadFailedRef.current); reloadFailedRef.current = null; setError(null); }
    catch { setError({ message: 'بارگذاری ساختمان تأییدشده ناموفق بود؛ دوباره تلاش کنید.', retryable: true }); }
    finally { setBusy(false); }
  };

  if (!canReview) return null;
  return (
    <section className="candidate-review" ref={sectionRef} aria-label="بررسی ساختمان‌های پیشنهادی" dir="rtl">
      <label className="candidate-toggle" htmlFor="candidate-layer">
        <input id="candidate-layer" type="checkbox" role="switch" checked={enabled}
          onChange={(event) => { setEnabled(event.target.checked); setSelectedId(null); }} />
        پیشنهادی {count != null && <span className="candidate-count">{number(count)} در انتظار</span>}
      </label>
      {loading && <p role="status">در حال بارگذاری پیشنهادها...</p>}
      {error && <div className="candidate-error" role={error.retryable ? 'alert' : undefined}>
        <p>{error.message}</p>
        {error.retryable && <button type="button" onClick={retry} disabled={busy || blocked}>تلاش دوباره</button>}
      </div>}
      {notice && <p role="status">{notice}</p>}
      {enabled && !loading && !error && !notice && !rows.length && <p>پیشنهادی در این نما نیست.</p>}
      {enabled && !notice && <button type="button" ref={nextRef} onClick={next}
        aria-disabled={!rows.length} disabled={blocked || busy}>پیشنهاد بعدی (N)</button>}
      {enabled && selected && (
        <div className="candidate-details" ref={panelRef} role="group" tabIndex={-1} aria-label="جزئیات پیشنهاد">
          <h3>ساختمان پیشنهادی</h3>
          <dl><dt>مساحت</dt><dd>{number(selected.area_m2)} متر مربع</dd>
            <dt>اطمینان مدل</dt><dd>{confidence(selected.confidence)}</dd>
            <dt>اطمینان بازبینی</dt><dd>{confidence(selected.verify_confidence)}</dd></dl>
          <a href={`https://www.google.com/maps/@?api=1&map_action=map&center=${selected.lat},${selected.lng}&zoom=21&basemap=satellite`}
            target="_blank" rel="noopener noreferrer">تصویر ماهواره‌ای این نقطه در گوگل</a>
          <label htmlFor="candidate-reason">دلیل رد</label>
          <select id="candidate-reason" value={reason} onChange={(event) => setReason(event.target.value)}>
            {['ساختمان نیست', 'هم‌پوشانی با ساختمان موجود', 'محدودهٔ ساختمان اشتباه است', 'نیاز به بررسی بیشتر'].map((text) => <option key={text}>{text}</option>)}
          </select>
          <div className="candidate-actions">
            <button type="button" onClick={() => review('accept')} disabled={blocked || busy}>تأیید (A)</button>
            <button type="button" onClick={() => review('reject')} disabled={blocked || busy}>رد (R)</button>
          </div>
          <p>{busy ? 'در حال ثبت بررسی...' : 'کلیدهای A: تأیید، R: رد، N: بعدی'}</p>
          {blocked && <p>برای بررسی، رسم یا اجرای طراحی را تمام کنید و به نسخهٔ کاری برگردید.</p>}
        </div>
      )}
    </section>
  );
}
